import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  startDownloadRecordExcelErrors,
  startDownloadRecordExcelTemplate,
  startRecordExcelUpdate
} from "../../../app/actions/recordExcelUpdateActions";

const MATRIX_MODE = {
  DRI: "dri",
  PROSPECTO: "prospecto",
  TERM: "term"
};

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "720px",
    width: "90%",
    maxHeight: "90vh",
    height: "auto",
    padding: 0,
    overflow: "auto"
  }
};

if (typeof document !== "undefined") {
  Modal.setAppElement("#root");
}

const isDriCasePhase = (phase = "") => {
  const current = String(phase || "").trim().toLowerCase();
  return current === "dri_preliminar" || current === "dri_final";
};

const resolveDefaultMatrixMode = (roleKey = "", casePhase = "", subjectKind = "") => {
  if (String(subjectKind || "").trim() === "search_key_term") {
    return MATRIX_MODE.TERM;
  }
  if (String(roleKey || "").trim().toLowerCase() !== "manager") {
    return MATRIX_MODE.PROSPECTO;
  }
  return isDriCasePhase(casePhase) ? MATRIX_MODE.DRI : MATRIX_MODE.PROSPECTO;
};

const RecordExcelUpdateModal = ({
  isOpen,
  toggle,
  moduleProcess,
  expectedName,
  expectedId,
  roleKey,
  casePhase = "",
  subjectKind = "",
  canApply = true,
  onApplied
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allowFromList = String(
    useSelector((state) => state.configuration?.allowLoadingRecordFromListEnabled)
  ).toLowerCase() !== "false";
  const matchMode = String(
    useSelector((state) => state.configuration?.matchPreManagerToManagerData) || "id"
  ).trim().toLowerCase();
  const isManagerRole = String(roleKey || "").trim().toLowerCase() === "manager";
  const isTermRecord = String(subjectKind || "").trim() === "search_key_term";
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [result, setResult] = useState(null);
  const [acceptedValidation, setAcceptedValidation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedRowNumber, setSelectedRowNumber] = useState("");
  const [matrixMode, setMatrixMode] = useState(() => resolveDefaultMatrixMode(roleKey, casePhase, subjectKind));

  useEffect(() => {
    if (isOpen) {
      setMatrixMode(resolveDefaultMatrixMode(roleKey, casePhase, subjectKind));
    }
  }, [isOpen, roleKey, casePhase, subjectKind]);

  const bag = result?.bagRowProcess || [];
  const allValid = useMemo(
    () => Boolean(result?.ok && result.numberOfRowsValid === 1 && result.numberOfRowsInvalid === 0),
    [result]
  );
  const errors = bag.flatMap((row) => row.stepsErrorsList || []);
  const resolvedMatrixMode = isTermRecord || isManagerRole ? matrixMode : "";

  const reset = () => {
    setFile(null);
    setResult(null);
    setAcceptedValidation(false);
    setSelectedRowNumber("");
    setMatrixMode(resolveDefaultMatrixMode(roleKey, casePhase, subjectKind));
    setFileInputKey((key) => key + 1);
  };

  const handleMatrixMode = (nextMode) => {
    if (nextMode === matrixMode) {
      return;
    }
    setMatrixMode(nextMode);
    setFile(null);
    setResult(null);
    setAcceptedValidation(false);
    setSelectedRowNumber("");
    setFileInputKey((key) => key + 1);
  };

  const handleClose = () => {
    reset();
    toggle();
  };

  const handleValidate = async (rowNumber = "") => {
    if (!file) {
      Swal.fire(t("titles.error"), t("record-excel-update.error.file"), "error");
      return;
    }
    const nextRow = /^\d+$/.test(String(rowNumber || "")) ? String(rowNumber) : "";
    setSelectedRowNumber(nextRow);
    setBusy(true);
    const body = await dispatch(startRecordExcelUpdate({
      process: moduleProcess,
      file,
      apply: false,
      expectedName,
      expectedId,
      roleKey,
      matrixMode: resolvedMatrixMode,
      subjectKind,
      selectedRowNumber: nextRow
    }));
    setBusy(false);
    setAcceptedValidation(false);
    setResult(body);
    if (body?.selectedRowNumber) {
      setSelectedRowNumber(String(body.selectedRowNumber));
    }
    if (!body.ok && !body.bagRowProcess) {
      Swal.fire(t("titles.error"), body.msg || t("record-excel-update.error.generic"), "error");
    }
  };

  const handleApply = async () => {
    if (!canApply || !allValid || !acceptedValidation || !file) {
      return;
    }
    if (result?.needsRowChoice && !selectedRowNumber) {
      return;
    }
    const confirm = await Swal.fire({
      title: t("record-excel-update.confirm.title"),
      text: isTermRecord && matrixMode === MATRIX_MODE.DRI
        ? t(
          "record-excel-update.confirm.text-term-dri",
          "Se guardará el DRI de la fila que coincide con este término clave y quedará ligado a ese id."
        )
        : isTermRecord
          ? t(
            "record-excel-update.confirm.text-term",
            "Se actualizará el término clave con la fila que coincide con su id."
          )
          : isManagerRole && matrixMode === MATRIX_MODE.PROSPECTO
            ? t("record-excel-update.confirm.text-prospecto")
            : t("record-excel-update.confirm.text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("record-excel-update.confirm.apply"),
      cancelButtonText: t("titles.cancel", "Cancelar")
    });
    if (!confirm.isConfirmed) {
      return;
    }
    setBusy(true);
    const body = await dispatch(startRecordExcelUpdate({
      process: moduleProcess,
      file,
      apply: true,
      expectedName,
      expectedId,
      roleKey,
      matrixMode: resolvedMatrixMode,
      subjectKind,
      selectedRowNumber
    }));
    setBusy(false);
    setResult(body);
    if (body.ok && body.applied) {
      Swal.fire(
        t("titles.success"),
        isTermRecord && matrixMode === MATRIX_MODE.DRI
          ? t("record-excel-update.success-term-dri", "El DRI quedó ligado al término clave.")
          : isTermRecord
            ? t("record-excel-update.success-term", "El término clave se actualizó.")
            : isManagerRole && matrixMode === MATRIX_MODE.PROSPECTO
              ? t("record-excel-update.success-prospecto")
              : t("record-excel-update.success"),
        "success"
      );
      onApplied?.();
      handleClose();
      return;
    }
    Swal.fire(t("titles.error"), body.msg || t("record-excel-update.error.generic"), "error");
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={customStyles}
      closeTimeoutMS={200}
      className="modal"
      overlayClassName="modal-fondo"
    >
      <div className="modal-header d-flex align-items-center w-100 px-3 pt-3">
        <h5 className="modal-title mb-0">{t("record-excel-update.title")}</h5>
        <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={handleClose}></button>
      </div>
      <div className="modal-body px-3">
        <div className="alert alert-warning">
          {t(allowFromList ? "record-excel-update.legend-list" : "record-excel-update.legend")}
        </div>
        {(isManagerRole || isTermRecord) && (
          <div className="mb-3">
            <div
              className="btn-group"
              role="group"
              aria-label={t("record-excel-update.matrix.tabs", "Formato del Excel")}
            >
              {isTermRecord ? (
                <button
                  type="button"
                  className={`btn btn-sm ${matrixMode === MATRIX_MODE.TERM ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleMatrixMode(MATRIX_MODE.TERM)}
                >
                  {t("record-excel-update.matrix.term", "Término Clave")}
                </button>
              ) : null}
              <button
                type="button"
                className={`btn btn-sm ${matrixMode === MATRIX_MODE.DRI ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => handleMatrixMode(MATRIX_MODE.DRI)}
              >
                {t("record-excel-update.matrix.dri", "DRI")}
              </button>
              {isManagerRole && (
                <button
                  type="button"
                  className={`btn btn-sm ${matrixMode === MATRIX_MODE.PROSPECTO ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => handleMatrixMode(MATRIX_MODE.PROSPECTO)}
                >
                  {t("record-excel-update.matrix.prospecto", "Prospecto del Caso")}
                </button>
              )}
            </div>
            <p className="small text-muted mb-0 mt-2">
              {isTermRecord && matrixMode === MATRIX_MODE.TERM
                ? t(
                  "record-excel-update.matrix.hint-term",
                  "Descarga o actualiza el formato de término clave. Si el Excel trae varias filas, se toma la del id de este término."
                )
                : isTermRecord && matrixMode === MATRIX_MODE.DRI
                  ? t(
                    "record-excel-update.matrix.hint-term-dri",
                    "Descarga el formato DRI con el id de este término clave, o sube una fila o la matriz completa. La fila se elige por ese id y el DRI queda ligado al término."
                  )
                  : matrixMode === MATRIX_MODE.DRI
                    ? t("record-excel-update.matrix.hint-dri")
                    : t("record-excel-update.matrix.hint-prospecto")}
            </p>
          </div>
        )}
        {!canApply && (
          <p className="small text-warning">
            {t(
              "record-excel-update.disabled",
              "No se puede actualizar desde Excel mientras el registro no es editable"
            )}
          </p>
        )}
        <p className="small text-muted">
          {t(allowFromList ? "record-excel-update.howto-list" : "record-excel-update.howto")}
        </p>
        {allowFromList && (
          <p className="small text-muted">
            {t(`record-excel-update.match-hint.${matchMode === "name" || matchMode === "id-name" ? matchMode : "id"}`)}
          </p>
        )}
        <div className="d-flex gap-2 mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!expectedId && !expectedName}
            onClick={async () => {
              const body = await dispatch(startDownloadRecordExcelTemplate(
                moduleProcess,
                expectedName,
                roleKey,
                expectedId,
                resolvedMatrixMode,
                subjectKind
              ));
              if (!body.ok) {
                Swal.fire(t("titles.error"), body.msg || t("record-excel-update.error.generic"), "error");
              }
            }}
          >
            <i className="fa fa-download me-1" />
            {isTermRecord && matrixMode === MATRIX_MODE.TERM
              ? t("record-excel-update.download-template-term", "Descargar formato de término clave")
              : isTermRecord && matrixMode === MATRIX_MODE.DRI
                ? t("record-excel-update.download-template-term-dri", "Descargar formato DRI")
                : isManagerRole && matrixMode === MATRIX_MODE.DRI
                  ? t("record-excel-update.download-template-dri")
                  : isManagerRole && matrixMode === MATRIX_MODE.PROSPECTO
                    ? t("record-excel-update.download-template-prospecto")
                    : t("record-excel-update.download-template")}
          </button>
        </div>
        <input
          key={fileInputKey}
          type="file"
          accept=".xlsx"
          className="form-control"
          onChange={(event) => {
            setFile(event.target.files?.[0] || null);
            setResult(null);
            setAcceptedValidation(false);
            setSelectedRowNumber("");
          }}
        />
        {(expectedId || expectedName) && (
          <p className="mt-2 small">
            {expectedId
              ? <>{t("record-excel-update.expected-id", "Id esperado del registro")}: <strong>{expectedId}</strong></>
              : null}
            {expectedId && expectedName ? " · " : null}
            {expectedName
              ? <>{t("record-excel-update.expected-name", "Nombre esperado del registro")}: <strong>{expectedName}</strong></>
              : null}
          </p>
        )}
        {Array.isArray(result?.candidateRows) && result.candidateRows.length > 1 && (
          <div className="mt-3">
            <p className="small mb-2">
              {t(
                "record-excel-update.term-dri.choose",
                "Este término clave aparece en {{count}} filas. Elige el DRI que quieres guardar.",
                { count: result.candidateRows.length }
              )}
            </p>
            <div style={{ maxHeight: 220, maxWidth: "100%", overflow: "auto" }}>
              <table
                className="table table-sm table-bordered align-middle mb-0"
                style={{ minWidth: 720, whiteSpace: "nowrap" }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 36, position: "sticky", top: 0, background: "#fff" }} />
                    <th style={{ position: "sticky", top: 0, background: "#fff" }}>
                      {t("record-excel-update.term-dri.col.row", "Fila")}
                    </th>
                    <th style={{ position: "sticky", top: 0, background: "#fff" }}>
                      {t("record-excel-update.term-dri.col.name", "Nombre")}
                    </th>
                    <th style={{ position: "sticky", top: 0, background: "#fff" }}>
                      {t("record-excel-update.term-dri.col.dri", "DRI")}
                    </th>
                    <th style={{ position: "sticky", top: 0, background: "#fff" }}>
                      {t("record-excel-update.term-dri.col.permission", "Permiso")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.candidateRows.map((row) => (
                    <tr key={row.rowNumber}>
                      <td>
                        <input
                          type="radio"
                          name="term-dri-row"
                          aria-label={t(
                            "record-excel-update.term-dri.pick",
                            "Elegir fila {{row}}",
                            { row: row.rowNumber }
                          )}
                          checked={Number(selectedRowNumber) === Number(row.rowNumber)}
                          disabled={busy}
                          onChange={() => handleValidate(row.rowNumber)}
                        />
                      </td>
                      <td>{row.rowNumber}</td>
                      <td>{row.name || "—"}</td>
                      <td title={row.dri || ""}>{row.dri || "—"}</td>
                      <td>{row.permission || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {result?.ok && result.matchedFromList && !(result.candidateRows || []).length && (
          <p className="mt-2 small text-info">
            {t("record-excel-update.matched-from-list", {
              row: result.matchedRowNumber,
              total: result.fileRowCount,
              name: expectedName
            })}
          </p>
        )}
        {result?.ok && bag[0]?.overlayKeys?.length > 0 && (
          <p className="mt-2 small text-success">
            {t("record-excel-update.diff-summary", {
              count: bag[0].overlayKeys.length,
              skipped: (bag[0].unchangedKeys || []).length
            })}
          </p>
        )}
        {errors.length > 0 && (
          <div className="mt-3 border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
            <div className="fw-semibold mb-2">{t("record-excel-update.errors-title")}</div>
            {errors.map((item, index) => (
              <div key={`${item.field}-${index}`} className="small text-danger">
                {item.field}: {item.message}
              </div>
            ))}
          </div>
        )}
        {bag.length > 0 && (
          <button
            type="button"
            className="btn btn-link btn-sm px-0 mt-2"
            onClick={() => dispatch(startDownloadRecordExcelErrors(moduleProcess, bag, roleKey, resolvedMatrixMode, subjectKind))}
          >
            {t("record-excel-update.download-errors")}
          </button>
        )}
        <div className="form-check mt-3">
          <input
            id="record-excel-valid"
            type="checkbox"
            className="form-check-input"
            disabled={!allValid}
            checked={acceptedValidation}
            onChange={(event) => setAcceptedValidation(event.target.checked)}
          />
          <label className="form-check-label" htmlFor="record-excel-valid">
            {t("record-excel-update.checkbox")}
          </label>
        </div>
      </div>
      <div className="modal-footer px-3 pb-3">
        <button type="button" className="btn btn-light" onClick={handleClose}>
          {t("titles.cancel", "Cancelar")}
        </button>
        <button type="button" className="btn btn-outline-primary" disabled={busy || !file} onClick={() => handleValidate()}>
          {t("record-excel-update.validate")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !canApply || !allValid || !acceptedValidation}
          onClick={handleApply}
        >
          {t("record-excel-update.import")}
        </button>
      </div>
    </Modal>
  );
};

RecordExcelUpdateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  moduleProcess: PropTypes.string.isRequired,
  expectedName: PropTypes.string,
  expectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  roleKey: PropTypes.string,
  casePhase: PropTypes.string,
  subjectKind: PropTypes.string,
  canApply: PropTypes.bool,
  onApplied: PropTypes.func
};

export default RecordExcelUpdateModal;
